/**
 * The small part of Markdown agent messages use: headings, paragraphs,
 * bullet and numbered lists (nested by indent) and bold. Parsed to plain
 * data so the message is rendered as React elements and never as HTML.
 */

export type ChatInline = { text: string; bold: boolean };

export type ChatListItem = { text: string; children: ChatList[] };

export type ChatList = { ordered: boolean; items: ChatListItem[] };

export type ChatBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'paragraph'; lines: string[] }
  | { kind: 'list'; list: ChatList };

const HEADING = /^#{1,6}\s+(.*)$/;
const LIST_ITEM = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/;
/** Spaces of indent per nested list level. */
const INDENT = 2;

/** The blocks of a message, in order. */
export const parseChatBlocks = (markdown: string): ChatBlock[] => {
  const blocks: ChatBlock[] = [];
  // The open lists of the current list block, outermost first.
  let lists: ChatList[] = [];
  let paragraph: string[] | undefined;

  const close = () => {
    lists = [];
    paragraph = undefined;
  };

  for (const line of markdown.replace(/\r\n?/g, '\n').split('\n')) {
    if (!line.trim()) {
      close();
      continue;
    }
    const heading = HEADING.exec(line.trim());
    if (heading) {
      close();
      blocks.push({ kind: 'heading', text: (heading[1] ?? '').trim() });
      continue;
    }
    const item = LIST_ITEM.exec(line);
    if (item) {
      paragraph = undefined;
      const [, indent = '', marker = '', itemText = ''] = item;
      const ordered = /\d/.test(marker);
      const depth = Math.min(
        Math.floor(indent.replace(/\t/g, '  ').length / INDENT),
        lists.length,
      );
      const parent = lists[depth - 1]?.items.at(-1);
      if (depth === 0 && lists[0]?.ordered !== ordered) {
        const list: ChatList = { ordered, items: [] };
        lists = [list];
        blocks.push({ kind: 'list', list });
      } else if (depth === lists.length && parent) {
        const nested: ChatList = { ordered, items: [] };
        parent.children.push(nested);
        lists = [...lists, nested];
      } else {
        lists = lists.slice(0, depth + 1);
      }
      lists.at(-1)?.items.push({ text: itemText, children: [] });
      continue;
    }
    const lastItem = lists.at(-1)?.items.at(-1);
    if (lastItem && /^\s/.test(line)) {
      lastItem.text = `${lastItem.text} ${line.trim()}`;
      continue;
    }
    lists = [];
    if (!paragraph) {
      paragraph = [];
      blocks.push({ kind: 'paragraph', lines: paragraph });
    }
    paragraph.push(line.trim());
  }
  return blocks;
};

/** The plain and bold runs of one line of text. */
export const parseChatInline = (text: string): ChatInline[] =>
  text
    .split(/\*\*(.+?)\*\*/)
    .map((part, i) => ({ text: part, bold: i % 2 === 1 }))
    .filter((part) => part.text !== '');
